<template>
  <div class="campo-busca-multiplo-container mb-3">
    <label
      v-if="labelExibido && !hideLabel"
      :for="id"
      class="form-label"
      :class="{ 'is-required': isObrigatorio }"
    >
      {{ labelExibido }}
      <i
        v-if="labelInfo"
        class="bi bi-info-circle text-primary ms-1"
        :title="labelInfo"
        style="cursor: help; font-size: 0.85em;"
      ></i>
    </label>

    <!-- Input + Chips combinados -->
    <div class="input-group has-validation">
      <div class="form-control form-control-sm chip-input-wrapper" @click="focusInput">
        <span
          v-for="(item, idx) in itensSelecionados"
          :key="item[idField] || idx"
          class="tag-badge d-inline-flex align-items-center gap-1"
        >
          <span class="small">{{ item[descField] || item[idField] }}</span>
          <i
            class="bi bi-x-circle tag-remove-icon"
            @click.stop="removerItem(idx)"
            role="button"
            aria-label="Remover"
          ></i>
        </span>
        <input
          :id="id"
          ref="inputRef"
          type="text"
          class="chip-input"
          v-model="textoBusca"
          :placeholder="itensSelecionados.length === 0 ? placeholderExibido : ''"
          @keydown.enter.prevent="adicionarTag"
          @keydown.delete="handleBackspace"
        />
      </div>
      <button
        class="btn btn-outline-secondary d-flex align-items-center"
        type="button"
        @click="abrirModalDeBusca"
        :disabled="isDisabled"
      >
        <i class="bi bi-search"></i>
      </button>
    </div>

    <div class="form-text mt-1" :class="{ invisible: itensSelecionados.length === 0 }">
      <template v-if="itensSelecionados.length > 0">
        <i class="bi bi-check-circle text-success me-1"></i>
        {{ itensSelecionados.length }} registro(s) selecionado(s)
      </template>
      <template v-else>
        &nbsp;
      </template>
    </div>

    <!-- Modal de busca com footer de selecionados -->
    <ModalDeBusca
      v-if="exibirModal"
      :configuracao="configuracaoBusca"
      :selecao-multipla="true"
      :z-index-base="zIndexModalBusca"
      :itens-ja-selecionados="itensSelecionados"
      :on-show-notification="(payload) => emit('show-notification', payload)"
      @fechar="fecharModalDeBusca"
      @itens-selecionados="onItensSelecionados"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import api from '@/services/api'
import ModalDeBusca from '../modal/ModalDeBusca.vue'

const props = defineProps({
  id: { type: String, required: true },
  label: { type: String, default: '' },
  modelValue: { type: Array, default: () => [] },
  itensSelecionados: { type: Array, default: () => [] },
  configuracaoBusca: { type: Object, required: true },
  required: { type: [Boolean, String, Number], default: false },
  hideLabel: { type: Boolean, default: false },
  placeholder: { type: String, default: 'Digite e pressione Enter para adicionar...' },
  zIndexModalBusca: { type: Number, default: undefined },
  labelInfo: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue', 'update:itensSelecionados', 'selecionados', 'show-notification'])

const exibirModal = ref(false)
const textoBusca = ref('')
const itensInternos = ref([])
const inputRef = ref(null)

const focusInput = () => {
  inputRef.value?.focus()
}

const conf = computed(() => props.configuracaoBusca || {})
const labelExibido = computed(() => props.label || conf.value.label || '')
const placeholderExibido = computed(() => props.placeholder || conf.value.placeholder || 'Buscar...')
const idField = computed(() => conf.value.idField || 'id')
const descField = computed(() => conf.value.descField || idField.value)

const isDisabled = computed(() => false)

const isObrigatorio = computed(() => {
  return (props.required === true || props.required === 'true' || props.required === 'required' || props.required === '') &&
    itensInternos.value.length === 0
})

// ── Sincroniza itensInternos com props.itensSelecionados ──
watch(() => props.itensSelecionados, (novos) => {
  itensInternos.value = [...(novos || [])]
}, { immediate: true, deep: true })

// ── Adicionar tag via Enter ──
const adicionarTag = async () => {
  const termo = textoBusca.value.trim()
  if (!termo) return

  // Verifica se já existe
  const jaExiste = itensInternos.value.some(i =>
    String(i[idField.value]) === termo ||
    String(i[descField.value]).toLowerCase() === termo.toLowerCase()
  )
  if (jaExiste) {
    textoBusca.value = ''
    return
  }

  // Tenta buscar o item pela API
  try {
    const endpoint = conf.value.endpointApi || ''
    const response = await api.get(`/${endpoint}?busca=${encodeURIComponent(termo)}&pageSize=5`)
    const dados = response.data || []
    if (Array.isArray(dados) && dados.length > 0) {
      // Pega o primeiro resultado
      const item = dados[0]
      itensInternos.value.push(item)
      emitChanges()
    } else {
      // Se não encontrou, adiciona como termo livre (apenas o texto)
      const item = { [idField.value]: termo, [descField.value]: termo }
      itensInternos.value.push(item)
      emitChanges()
    }
  } catch {
    // Fallback: adiciona como termo livre
    const item = { [idField.value]: termo, [descField.value]: termo }
    itensInternos.value.push(item)
    emitChanges()
  }

  textoBusca.value = ''
}

// ── Backspace para remover última tag ──
const handleBackspace = () => {
  if (textoBusca.value === '' && itensInternos.value.length > 0) {
    itensInternos.value.pop()
    emitChanges()
  }
}

// ── Remover item específico ──
const removerItem = (idx) => {
  itensInternos.value.splice(idx, 1)
  emitChanges()
}

// ── Modal ──
const abrirModalDeBusca = () => { exibirModal.value = true }
const fecharModalDeBusca = () => { exibirModal.value = false }

const onItensSelecionados = (itens) => {
  // O ModalDeBusca emite a SELEÇÃO COMPLETA (o Map interno é inicializado com os
  // itens já selecionados e alterado por toggle a cada marcar/desmarcar). Portanto,
  // devemos SUBSTITUIR o estado interno pela lista emitida — não mesclar — para que
  // desmarcações no modal também sejam refletidas no campo.
  itensInternos.value = Array.isArray(itens) ? [...itens] : []
  emitChanges()
  fecharModalDeBusca()
}

// ── Emite mudanças ──
const emitChanges = () => {
  emit('update:modelValue', itensInternos.value.map(i => i[idField.value]))
  emit('update:itensSelecionados', [...itensInternos.value])
  emit('selecionados', [...itensInternos.value])
}

// ── Carrega descrições dos itens iniciais (quando modelValue tem IDs mas itensSelecionados não) ──
onMounted(async () => {
  if (props.modelValue.length > 0 && itensInternos.value.length === 0) {
    for (const id of props.modelValue) {
      try {
        const response = await api.get(`/${conf.value.endpointApi}/${id}`)
        if (response.data) {
          itensInternos.value.push(response.data)
        }
      } catch {
        // ignora itens que não puderam ser carregados
      }
    }
    emitChanges()
  }
})
</script>

<style scoped>
.form-label { font-weight: 400 !important; }
.form-label.is-required::after { content: " *"; color: #dc3545; font-weight: bold; }
.campo-busca-multiplo-container { display: flex; flex-direction: column; }

/* ── Wrapper que simula o form-control com chips dentro ── */
.chip-input-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  min-height: calc(1.5em + 0.5rem + 2px);
  cursor: text;
}

.chip-input-wrapper:focus-within {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* ── Input interno sem bordas, flexível ── */
.chip-input {
  border: none;
  outline: none;
  box-shadow: none;
  flex: 1 1 80px;
  min-width: 60px;
  padding: 0;
  font-size: inherit;
  background: transparent;
}

.chip-input::placeholder {
  color: var(--bs-secondary-color);
  opacity: 0.7;
}

/* ── Tag badges ── */
.tag-badge {
  background: var(--bs-tertiary-bg);
  color: var(--bs-body-color);
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  padding: 0 4px;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  font-weight: 600;
  white-space: nowrap;
}

.tag-remove-icon {
  cursor: pointer;
  font-size: 0.65rem;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.tag-remove-icon:hover {
  opacity: 1;
  color: var(--bs-danger);
}
</style>
